import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  GripVertical,
  Type,
  Calendar,
  Clock,
  Hash,
  ToggleLeft,
  List,
  Upload,
  Loader2,
  Settings,
  Eye,
  EyeOff,
  Users,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'textarea', label: 'Text Area', icon: FileText },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock },
  { value: 'single_select', label: 'Single Select', icon: List },
  { value: 'multi_select', label: 'Multiple Select', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft },
  { value: 'file', label: 'File Upload', icon: Upload },
];

const ROLE_VISIBILITY_OPTIONS = [
  { value: 'everyone', label: 'Everyone', description: 'All employees can see' },
  { value: 'human_resources', label: 'Human Resources Management', description: 'Only Human Resources Management department employees' },
  { value: 'administration', label: 'Administration', description: 'Only Administration / Administration (Travel) department employees' },
  { value: 'hr_and_admin', label: 'HR & Admin', description: 'Both Human Resources and Administration departments' },
];

const getFieldIcon = (type) => {
  const fieldType = FIELD_TYPES.find(f => f.value === type);
  return fieldType?.icon || Type;
};

const getRoleVisibilityLabel = (value) => {
  // Handle legacy values
  const normalizedValue = value === 'only_hr' ? 'human_resources' 
    : value === 'only_admin_team' ? 'administration' : value;
  const option = ROLE_VISIBILITY_OPTIONS.find(o => o.value === normalizedValue);
  return option?.label || 'Everyone';
};

const getRoleVisibilityBadgeColor = (value) => {
  switch (value) {
    case 'human_resources':
    case 'only_hr': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'administration':
    case 'only_admin_team': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'hr_and_admin': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    default: return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
};

// Form Builder Dialog Component
const FormBuilderDialog = ({
  open,
  onOpenChange,
  title,
  onSubmit,
  submitLabel,
  actionLoading,
  formData,
  setFormData,
  fieldData,
  setFieldData,
  showFieldBuilder,
  setShowFieldBuilder,
  editingFieldIndex,
  setEditingFieldIndex,
  isDefault = false
}) => {
  const resetFieldData = () => {
    setFieldData({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: '',
    });
    setEditingFieldIndex(null);
  };

  const handleAddField = () => {
    if (!fieldData.name || !fieldData.label) {
      toast.error('Field name and label are required');
      return;
    }

    // Validate options for select-type fields
    if (['select', 'single_select', 'multi_select'].includes(fieldData.type)) {
      if (!fieldData.options || !fieldData.options.trim()) {
        toast.error('Please provide options for this field type');
        return;
      }
    }

    const newField = {
      ...fieldData,
      name: fieldData.name.toLowerCase().replace(/\s+/g, '_'),
      options: ['select', 'single_select', 'multi_select'].includes(fieldData.type) 
        ? fieldData.options.split(',').map(o => o.trim()).filter(o => o) 
        : []
    };

    if (editingFieldIndex !== null) {
      const updatedFields = [...formData.fields];
      updatedFields[editingFieldIndex] = newField;
      setFormData(prev => ({ ...prev, fields: updatedFields }));
    } else {
      setFormData(prev => ({ ...prev, fields: [...prev.fields, newField] }));
    }

    resetFieldData();
    setShowFieldBuilder(false);
  };

  const handleEditField = (index) => {
    const field = formData.fields[index];
    setFieldData({
      ...field,
      options: Array.isArray(field.options) ? field.options.join(', ') : ''
    });
    setEditingFieldIndex(index);
    setShowFieldBuilder(true);
  };

  const handleRemoveField = (index) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, fields: updatedFields }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isDefault ? 'Update settings for this request type' : 'Configure the request type and define custom form fields'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            {!isDefault && (
              <div className="space-y-2">
                <Label>Request Type Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Equipment Request"
                  data-testid="request-type-name"
                />
              </div>
            )}
            {isDefault && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">{formData.name}</p>
                <p className="text-xs text-muted-foreground">Default request type (name cannot be changed)</p>
              </div>
            )}
            
            {!isDefault && (
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this request type"
                  data-testid="request-type-description"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-xs text-muted-foreground">Enable or disable this type</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role Visibility</Label>
                <Select
                  value={formData.role_visibility}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role_visibility: value }))}
                >
                  <SelectTrigger data-testid="role-visibility-select">
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_VISIBILITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Form Fields - Only for custom types */}
          {!isDefault && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Form Fields</h4>
                    <p className="text-sm text-muted-foreground">Define custom fields for this request type</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      resetFieldData();
                      setShowFieldBuilder(true);
                    }}
                    data-testid="add-field-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Field
                  </Button>
                </div>

                {formData.fields?.length > 0 && (
                  <div className="space-y-2">
                    {formData.fields.map((field, index) => {
                      const Icon = getFieldIcon(field.type);
                      return (
                        <div
                          key={`field-${index}-${field.name}`}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                        >
                          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{field.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {field.type} • {field.required ? 'Required' : 'Optional'}
                            </p>
                          </div>
                          <Button size="icon" variant="ghost" onClick={() => handleEditField(index)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleRemoveField(index)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {(!formData.fields || formData.fields.length === 0) && !showFieldBuilder && (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No custom fields added yet</p>
                  </div>
                )}

                {showFieldBuilder && (
                  <Card className="border-primary/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        {editingFieldIndex !== null ? 'Edit Field' : 'New Field'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Field Name *</Label>
                          <Input
                            value={fieldData.name}
                            onChange={(e) => setFieldData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="field_name"
                            data-testid="field-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Label *</Label>
                          <Input
                            value={fieldData.label}
                            onChange={(e) => setFieldData(prev => ({ ...prev, label: e.target.value }))}
                            placeholder="Display Label"
                            data-testid="field-label-input"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Field Type</Label>
                          <Select
                            value={fieldData.type}
                            onValueChange={(value) => setFieldData(prev => ({ ...prev, type: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  <div className="flex items-center gap-2">
                                    <type.icon className="w-4 h-4" />
                                    {type.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>
                            {fieldData.type === 'checkbox' ? 'Checkbox Description' : 'Placeholder'}
                          </Label>
                          <Input
                            value={fieldData.placeholder}
                            onChange={(e) => setFieldData(prev => ({ ...prev, placeholder: e.target.value }))}
                            placeholder={fieldData.type === 'checkbox' 
                              ? "e.g., I agree to the terms" 
                              : "Enter placeholder text"}
                          />
                          {fieldData.type === 'checkbox' && (
                            <p className="text-xs text-muted-foreground">
                              This text will appear next to the checkbox
                            </p>
                          )}
                        </div>
                      </div>

                      {(fieldData.type === 'select' || fieldData.type === 'single_select' || fieldData.type === 'multi_select') && (
                        <div className="space-y-2">
                          <Label>Options (comma-separated) *</Label>
                          <Input
                            value={fieldData.options}
                            onChange={(e) => setFieldData(prev => ({ ...prev, options: e.target.value }))}
                            placeholder="Option 1, Option 2, Option 3"
                          />
                          <p className="text-xs text-muted-foreground">
                            Enter the options separated by commas
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <Label>Required Field</Label>
                        <Switch
                          checked={fieldData.required}
                          onCheckedChange={(checked) => setFieldData(prev => ({ ...prev, required: checked }))}
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleAddField}>
                          {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
                        </Button>
                        <Button variant="outline" onClick={() => {
                          resetFieldData();
                          setShowFieldBuilder(false);
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={actionLoading} data-testid="submit-request-type-btn">
            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function RequestTypesPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [allRequestTypes, setAllRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    role_visibility: 'everyone',
    fields: []
  });
  
  // Field builder states
  const [showFieldBuilder, setShowFieldBuilder] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [fieldData, setFieldData] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: '',
  });

  const isAdminHead = employee?.role === 'Admin Head' || employee?.role === 'Super Admin';

  const fetchAllRequestTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/request-types/all`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setAllRequestTypes(data);
      }
    } catch (error) {
      console.error('Fetch request types error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAdminHead) {
      fetchAllRequestTypes();
    }
  }, [isAdminHead, fetchAllRequestTypes]);

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      role_visibility: 'everyone',
      fields: []
    });
  };

  const resetFieldData = () => {
    setFieldData({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: '',
    });
    setEditingFieldIndex(null);
  };

  const handleCreateRequestType = async () => {
    if (!formData.name) {
      toast.error('Request type name is required');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/request-types`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Request type created successfully');
        setCreateDialog(false);
        resetFormData();
        fetchAllRequestTypes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create request type');
      }
    } catch (error) {
      toast.error('Failed to create request type');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRequestType = async () => {
    if (!selectedType) return;

    setActionLoading(true);
    try {
      // Use different endpoint for default vs custom types
      const endpoint = selectedType.is_default 
        ? `${API}/request-types/default/${selectedType.type_id}`
        : `${API}/request-types/${selectedType.type_id}`;
      
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Request type updated successfully');
        setEditDialog(false);
        setSelectedType(null);
        resetFormData();
        fetchAllRequestTypes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update request type');
      }
    } catch (error) {
      toast.error('Failed to update request type');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRequestType = async () => {
    if (!selectedType || selectedType.is_default) {
      toast.error('Cannot delete default request types');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/request-types/${selectedType.type_id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Request type deleted successfully');
        setDeleteDialog(false);
        setSelectedType(null);
        fetchAllRequestTypes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete request type');
      }
    } catch (error) {
      toast.error('Failed to delete request type');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (type) => {
    setSelectedType(type);
    // Normalize legacy role_visibility values
    let roleVis = type.role_visibility || 'everyone';
    if (roleVis === 'only_hr') roleVis = 'human_resources';
    if (roleVis === 'only_admin_team') roleVis = 'administration';
    setFormData({
      name: type.name,
      description: type.description || '',
      is_active: type.is_active,
      role_visibility: roleVis,
      fields: type.fields || []
    });
    setEditDialog(true);
  };

  const openDeleteDialog = (type) => {
    setSelectedType(type);
    setDeleteDialog(true);
  };

  if (!isAdminHead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Access denied. Only Admin Head can manage request types.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="request-types-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">All Request Types</h1>
            <p className="text-muted-foreground mt-1">
              Manage all request types and their visibility settings
            </p>
          </div>
          <Button onClick={() => setCreateDialog(true)} data-testid="create-request-type-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Custom Type
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{allRequestTypes.length}</p>
                  <p className="text-sm text-muted-foreground">Total Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{allRequestTypes.filter(t => t.is_active).length}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <EyeOff className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{allRequestTypes.filter(t => !t.is_active).length}</p>
                  <p className="text-sm text-muted-foreground">Inactive</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{allRequestTypes.filter(t => !t.is_default).length}</p>
                  <p className="text-sm text-muted-foreground">Custom Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Request Types Table */}
        <Card>
          <CardHeader>
            <CardTitle>Request Types Master Control</CardTitle>
            <CardDescription>
              View and manage all request types including their status and role visibility
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request Type Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Role Visibility</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRequestTypes.map((type) => (
                    <TableRow key={type.type_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          {type.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={type.is_default ? "secondary" : "outline"}>
                          {type.is_default ? 'Default' : 'Custom'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={type.is_active ? "default" : "secondary"} 
                          className={type.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : ''}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleVisibilityBadgeColor(type.role_visibility)}>
                          <Users className="w-3 h-3 mr-1" />
                          {getRoleVisibilityLabel(type.role_visibility)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(type)}
                            data-testid={`edit-${type.type_id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          {!type.is_default && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => openDeleteDialog(type)}
                              data-testid={`delete-${type.type_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <FormBuilderDialog
          open={createDialog}
          onOpenChange={setCreateDialog}
          title="Create Custom Request Type"
          onSubmit={handleCreateRequestType}
          submitLabel="Create"
          actionLoading={actionLoading}
          formData={formData}
          setFormData={setFormData}
          fieldData={fieldData}
          setFieldData={setFieldData}
          showFieldBuilder={showFieldBuilder}
          setShowFieldBuilder={setShowFieldBuilder}
          editingFieldIndex={editingFieldIndex}
          setEditingFieldIndex={setEditingFieldIndex}
          isDefault={false}
        />

        {/* Edit Dialog */}
        <FormBuilderDialog
          open={editDialog}
          onOpenChange={setEditDialog}
          title={`Edit: ${selectedType?.name || 'Request Type'}`}
          onSubmit={handleUpdateRequestType}
          submitLabel="Save Changes"
          actionLoading={actionLoading}
          formData={formData}
          setFormData={setFormData}
          fieldData={fieldData}
          setFieldData={setFieldData}
          showFieldBuilder={showFieldBuilder}
          setShowFieldBuilder={setShowFieldBuilder}
          editingFieldIndex={editingFieldIndex}
          setEditingFieldIndex={setEditingFieldIndex}
          isDefault={selectedType?.is_default || false}
        />

        {/* Delete Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Request Type</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedType?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteRequestType} disabled={actionLoading}>
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
